'use client';

import LargeCarousel from '@/src/components/carousel/large-carousel';
import ProductCarouselDesktop from '@/src/components/carousel/product-carousel.desktop';
import { AlertCircle, CircleAlert, CircleCheck, Minus, Plus } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { Link, usePathname, useRouter } from '@/src/i18n/navigation';
import React, { useEffect, useMemo, useState, useTransition } from 'react';
import AccordionList from '@/src/components/wrappers/accordion';
import { buildCDNImageUrl, PRODUCT_IMAGE_SIZE } from '@/src/lib/utils/command';
import { useParams, useSearchParams } from 'next/navigation';
import { extractIdFromSlug } from '@/src/lib/utils/slugify';

import { Button } from '@/src/components/ui/button';
import { AddToCartButton } from '@/src/components/Buttons/AddToCartButton';
import { signIn, useSession } from 'next-auth/react';
import { ResponseProductDetailsType } from '../service/getProductDetails';
import { SimilarProducts } from './similarProducts';
import useNavigation from '@/src/lib/hooks/useNavigation';
import {
  DiscountTypeEnums,
  VaraintOptinValueResponse,
  VaraintOptionResponse,
} from '@/src/types/catalog';
import Image from '@/src/components/Image';
import useCartStore from '@/src/lib/state-stores/cartStore';
import { useUnifiedCartStore } from '@/src/lib/state-stores/unifiedCartStore';
import { addOrUpdateUnifiedCartItemAction } from '@/src/app/[locale]/cart-checkout/actions/unifiedCart.actions';
import { Routes } from '@/src/routes/routes';
import IsFavorite from '../../../../components/IsFavorite';
import { Alert, AlertDescription } from '@/src/components/ui/alert';
import { toast } from 'sonner';
import SAR from '@/public/SAR';
import SaudiRealls from '@/src/components/sarComponent';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';
import Container from '@/src/components/ui/container';
import CurrentLanguageText from '@/src/components/helper/CurrentLanguageText';
import Typography from '@/src/components/ui/typography';
import { IDigitalCard } from '@/src/types/digitalCards';
import useRecentlyViewedStore from '@/src/lib/state-stores/recentlyViewedStore';

export const ProductDetails = ({ productInfo }: { productInfo: ResponseProductDetailsType }) => {
  const { addToCart, carts } = useCartStore();
  const localActive = useLocale();
  const t = useTranslations('product');
  const { createQueryString, removeQueryString } = useNavigation({ justUpdateUrl: true });
  const params = useSearchParams();
  const { data } = useSession();
  const pathname = usePathname();
  const router = useRouter();

  const isDigitalCard = pathname.includes(Routes.digitalCards);

  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [unselectedVariants, setUnselectedVariants] = useState<string[]>([]);
  const [disabledOptions, setDisabledOptions] = useState<string[]>([]);
  const [isAllowToAddToCart, setIsAllowToAddToCart] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [isPendingCart, startTransitionCart] = useTransition();
  const [error, setError] = useState(false);

  const routeParams = useParams();
  const routeStoreId = routeParams?.storeId ? extractIdFromSlug(routeParams.storeId as string) : '';
  const routeStoreName = routeParams?.store ? decodeURIComponent(routeParams.store as string) : '';

  useEffect(() => {
    if (productInfo?.integrationId) {
      const resolvedBranchId = String(
        productInfo?.branch?.id ||
          (productInfo as any)?.branchId ||
          (productInfo as any)?.branch_id ||
          routeStoreId ||
          '',
      );

      const resolvedStoreName =
        productInfo?.store?.englishName || productInfo?.store?.name || routeStoreName || '';

      useRecentlyViewedStore.getState().addItem({
        integrationId: productInfo.integrationId,
        defaultImage: productInfo.defaultImage,
        englishName: productInfo.englishName,
        arabicName: productInfo.arabicName,
        price: productInfo.price,
        originalPrice: productInfo.originalPrice,
        isHaveValidDiscount: productInfo.isHaveValidDiscount,
        discount: productInfo.discount as any,
        branchId: resolvedBranchId,
        branchName:
          productInfo?.branch?.englishName ||
          productInfo?.branch?.arabicName ||
          (productInfo?.branch as any)?.name ||
          '',
        storeId: String(productInfo?.store?.id || (productInfo as any)?.storeId || ''),
        storeName: productInfo?.store?.name || resolvedStoreName,
        storeEnglishName: resolvedStoreName,
        storeArabicName: productInfo?.store?.arabicName || productInfo?.store?.name || '',
        isSoldOut: productInfo.isSoldOut,
        currencyCode:
          (productInfo as any).currencyCode || productInfo?.branch?.currencyCode || 'SAR',
        countryId: (productInfo as any).countryId || productInfo?.branch?.countryId || 1,
        isDigitalCard: Boolean(isDigitalCard),
      } as any);
    }
  }, [productInfo, routeStoreId, routeStoreName, isDigitalCard]);

  useEffect(() => {
    const initialSelectedOptions: Record<string, string> = {};
    const initialUnselectedVariants: string[] = [];

    productInfo?.variantOptions?.forEach((variant) => {
      const queryKey = variant?.englishName?.toLowerCase() ?? '';
      const queryValue = params.get(queryKey) ?? ''; // Ensure queryValue is a string

      if (queryValue) {
        const option = variant.variantOptionValues?.find((opt) => opt.englishName === queryValue);
        if (option) {
          initialSelectedOptions[variant.integrationId as string] = option.integrationId as string;
        } else {
          initialUnselectedVariants.push(variant.integrationId as string);
        }
      } else {
        initialUnselectedVariants.push(variant.integrationId as string);
      }
    });
    setSelectedOptions(initialSelectedOptions);
  }, [params, productInfo?.variantOptions]);

  const currentVariant = useMemo(() => {
    return productInfo?.variants?.find((variant) => {
      return variant.selectedOptionValues?.every((option) => {
        if (option.varaintOptionId) {
          const selectedOptionIntegrationId = selectedOptions[option.varaintOptionId];
          return option.varaintOptionValueId === selectedOptionIntegrationId;
        }
        return false;
      });
    });
  }, [selectedOptions, productInfo?.variants]);

  const branchId = productInfo?.branch?.id;
  const currentVariantId = currentVariant ? String(currentVariant.integrationId) : null;

  const unifiedCartData = useUnifiedCartStore((s) => s.data);

  useEffect(() => {
    useUnifiedCartStore.getState().fetchCart();
  }, []);

  const allUnifiedItems = useMemo(() => {
    if (!unifiedCartData) return [];
    const fromStores = unifiedCartData.stores?.flatMap((g) => g.items) || [];
    const fromUnfulfilled = Array.isArray(unifiedCartData.unfulfilledItems)
      ? unifiedCartData.unfulfilledItems.flatMap((entry: any) =>
          'items' in entry && Array.isArray(entry.items) ? entry.items : [entry],
        )
      : [];
    return [...fromStores, ...fromUnfulfilled];
  }, [unifiedCartData]);

  const unifiedCartItem = useMemo(() => {
    const hasVariants = Boolean(productInfo?.variants && productInfo.variants.length > 0);
    return allUnifiedItems.find((item) => {
      const matchProduct =
        String(item.productId) === String(productInfo.integrationId) ||
        String(item.itemId) === String(productInfo.integrationId) ||
        ((productInfo as any)?.id && String(item.productId) === String((productInfo as any).id));

      if (!matchProduct) return false;

      if (hasVariants) {
        if (!currentVariantId) return false;
        return item.variantId !== null && String(item.variantId) === String(currentVariantId);
      }
      return !item.variantId;
    });
  }, [
    allUnifiedItems,
    productInfo.integrationId,
    (productInfo as any)?.id,
    productInfo?.variants,
    currentVariantId,
  ]);

  const filteredCarts = carts?.filter((cart) => cart?.branch?.id == branchId);

  const legacyCartItem = filteredCarts
    ?.flatMap((cart) => cart.cartItems)
    ?.find((item) => {
      if (item.itemTypeId === 2) {
        if (item.variantId !== null && currentVariantId !== null) {
          return String(item.variantId) === String(currentVariantId);
        } else {
          return false;
        }
      } else {
        return String(item.itemId) === String(productInfo.integrationId) || false;
      }
    });

  const cartItem = unifiedCartItem || legacyCartItem;

  useEffect(() => {
    if (cartItem) {
      setQuantity(cartItem.quantity);
    } else {
      setQuantity(1);
    }
  }, [cartItem]);

  const handleAddCard = async () => {
    const unselectedVariantIds =
      productInfo.variantOptions
        ?.filter((variant) => variant.integrationId && !selectedOptions[variant.integrationId])
        .map((variant) => variant.integrationId)
        .filter((id): id is string => id !== null && id !== undefined) || [];

    if (unselectedVariantIds.length > 0 && isAllowToAddToCart) {
      setUnselectedVariants(unselectedVariantIds);
      setError(true);
      throw new Error('Unselected variants');
    }

    try {
      // Clear error before new request
      useCartStore.getState().setError(null);

      trackEvent(AmplitudeEvents.product_added_to_cart_clicked, {
        shop_id: Number(productInfo?.store?.id),
        shop_name: `${productInfo?.store?.englishName}`,
        branch_id: Number(productInfo?.branch?.id),
        product_id: `${productInfo.integrationId}`,
        product_name: `${localActive === 'ar' ? productInfo?.arabicName : productInfo?.englishName}`,
        product_price: Number(
          (currentVariant?.price && currentVariant.price * quantity)?.toFixed(2),
        ),
        product_quantity: quantity,

        ...(currentVariant?.selectedOptionValues && {
          product_variants: currentVariant?.selectedOptionValues.map((item) => ({
            option_name: item?.varaintOptionEnglsihName,
            variant_name: item?.varaintOptionValueEnglishName || '',
          })),
        }),
      });

      const storeId = Number(productInfo?.store?.id || (productInfo as any)?.storeId);
      const hasVariants = Boolean(productInfo?.variants && productInfo.variants.length > 0);
      const productTypeId = hasVariants ? 2 : 1;
      const variantId = hasVariants ? currentVariant?.integrationId : undefined;
      const isUpdate = Boolean(cartItem);

      // Call unified store action
      const response = await useUnifiedCartStore.getState().handleAddOrUpdateItem(
        {
          storeId,
          storeName: productInfo?.store?.name,
          storeEnglishName: productInfo?.store?.englishName,
          storeArabicName: productInfo?.store?.arabicName,
          branchId: productInfo?.branch?.id,
          branchName:
            (productInfo?.branch as any)?.name ||
            productInfo?.branch?.englishName ||
            productInfo?.branch?.arabicName ||
            '',
          productId: String(productInfo.integrationId),
          variantId: variantId ? String(variantId) : undefined,
          productTypeId,
          itemTypeId: 1,
          quantity,
          price: Number(currentVariant?.price || productInfo.price || 0),
          originalPrice: Number(
            currentVariant?.originalPrice || productInfo.originalPrice || productInfo.price || 0,
          ),
          englishName: productInfo.englishName || '',
          arabicName: productInfo.arabicName || '',
          defaultImage: (currentVariant as any)?.defaultImage || productInfo.defaultImage || null,
          selectedVariantOptions: (currentVariant?.selectedOptionValues as any) || null,
        },
        {
          storeId,
          itemId: productInfo.integrationId ?? '',
          quantity,
          productTypeId,
          itemTypeId: 1,
          variantId: variantId ? String(variantId) : undefined,
        },
      );

      if (!response) {
        toast(t('ErrorToAdd'), {
          icon: <CircleAlert className="text-warning" />,
        });
        throw new Error('Failed to add');
      } else {
        toast(isUpdate ? t('ProductUpdatedSuccessfully') : t('ProductAddedSuccessfully'), {
          icon: <CircleCheck className="text-success" />,
        });

        try {
          useCartStore.getState().fetchCart();
        } catch (e) {
          // ignore
        }

        try {
          useUnifiedCartStore
            .getState()
            .fetchCartItemsCount()
            .catch(() => {});
        } catch (e) {
          // ignore
        }

        trackEvent(AmplitudeEvents.product_added_to_cart, {
          shop_id: Number(productInfo?.store?.id),
          shop_name: `${productInfo?.store?.englishName}`,
          branch_id: Number(productInfo?.branch?.id),
          product_id: `${productInfo.integrationId}`,
          product_name: `${localActive === 'ar' ? productInfo?.arabicName : productInfo?.englishName}`,
          product_price: Number(
            (currentVariant?.price && currentVariant.price * quantity)?.toFixed(2),
          ),
          status: 'success',
          product_quantity: quantity,

          ...(currentVariant?.selectedOptionValues && {
            product_variants: currentVariant?.selectedOptionValues.map((item) => ({
              option_name: item?.varaintOptionEnglsihName,
              variant_name: item?.varaintOptionValueEnglishName || '',
            })),
          }),
        });
      }
    } catch (error) {
      if ((error as Error).message !== 'Unselected variants') {
        toast(t('ErrorToAdd'), {
          icon: <CircleAlert className="text-warning" />,
        });
      }

      trackEvent(AmplitudeEvents.product_added_to_cart, {
        shop_id: Number(productInfo?.store?.id),
        shop_name: `${productInfo?.store?.englishName}`,
        branch_id: Number(productInfo?.branch?.id),
        product_id: `${productInfo.integrationId}`,
        product_name: `${localActive === 'ar' ? productInfo?.arabicName : productInfo?.englishName}`,
        product_price: Number(
          (currentVariant?.price && currentVariant.price * quantity)?.toFixed(2),
        ),
        status: 'failed',
        product_quantity: quantity,

        ...(currentVariant?.selectedOptionValues && {
          product_variants: currentVariant?.selectedOptionValues.map((item) => ({
            option_name: item?.varaintOptionEnglsihName,
            variant_name: item?.varaintOptionValueEnglishName || '',
          })),
        }),
      });
      throw error;
    }
  };

  // const handleAddCard = (e: React.MouseEvent<HTMLButtonElement>) => {
  //   const unselectedVariantIds =
  //     productInfo.variantOptions
  //       ?.filter((variant) => variant.integrationId && !selectedOptions[variant.integrationId])
  //       .map((variant) => variant.integrationId)
  //       .filter((id): id is string => id !== null && id !== undefined) || [];

  //   if (unselectedVariantIds.length > 0 && isAllowToAddToCart) {
  //     setUnselectedVariants(unselectedVariantIds);

  //     setError(true);
  //     return;
  //   }
  //   startTransitionCart(async () => {
  //     await addToCart({
  //       branchId: (productInfo?.branch?.id as string) || productInfo.branchId.toString(),
  //       itemId: productInfo.integrationId ?? '',
  //       quantity: quantity,
  //       itemTypeId: productInfo?.variants && productInfo?.variants?.length > 0 ? 2 : 1,
  //       variantId: productInfo?.variants
  //         ? productInfo.variants.length > 0
  //           ? currentVariant?.integrationId
  //           : undefined
  //         : undefined,
  //     });

  //     if (Boolean(errorFormCartApi)) {
  //       toast(t('ErrorToAdd'), {
  //         icon: <CircleAlert className="text-warning" />,
  //       });
  //     } else {
  //       trackEvent(AmplitudeEvents.PRODUCT_ADDED_TO_CART, {
  //         storeId: Number(productInfo?.store?.id),
  //         storeName: `${productInfo?.store?.englishName}`,
  //         branchId: Number(productInfo?.branch?.id),
  //         productId: `${productInfo.integrationId}`,
  //         productName: `${localActive === 'ar' ? productInfo?.arabicName : productInfo?.englishName}`,
  //         productPrice: Number(
  //           (currentVariant?.price && currentVariant.price * quantity)?.toFixed(2),
  //         ),
  //         productQuantity: quantity,
  //       });
  //       toast(t('ProductAddedSuccessfully'), {
  //         description: t('GoToCart'),
  //         icon: <CircleCheck className="text-success" />,
  //       });
  //     }
  //   });
  // };

  const handleSelect = (variant: VaraintOptionResponse, option: VaraintOptinValueResponse) => {
    const queryKey = variant?.englishName?.toLowerCase() ?? '';
    const queryValue = option.englishName ?? '';
    const isSelected = queryValue === params.get(queryKey);

    startTransition(() => {
      if (isSelected) {
        removeQueryString(queryKey);
      } else {
        createQueryString({ [queryKey]: queryValue });
      }
    });
  };

  // Extract all variantOptionValueIds from integertation array
  let variantOptionValueIds = new Set(
    productInfo?.variants?.flatMap((integration) =>
      integration?.selectedOptionValues?.map((value) => value.varaintOptionValueId),
    ),
  );

  // Filter variantOptions based on variantOptionValueIds
  let filteredVariantOptions = productInfo?.variantOptions?.map((option) => ({
    ...option,
    variantOptionValues: option?.variantOptionValues?.filter((value) =>
      variantOptionValueIds.has(value.integrationId),
    ),
  }));

  const checkSelectedOptions = (selectedOptions: Record<string, string>): boolean => {
    return productInfo?.variants?.some((integration) =>
      integration?.selectedOptionValues?.every((value) => {
        const selectedValue = selectedOptions[value.varaintOptionId as string];
        // If selectedValue is undefined, we consider it as partially matched.
        return selectedValue === undefined || selectedValue === value.varaintOptionValueId;
      }),
    ) as boolean;
  };

  useEffect(() => {
    if (!!filteredVariantOptions?.length) {
      const checkIfAllHasVarians = filteredVariantOptions.some(
        (item) => item.variantOptionValues?.length,
      );
      if (checkIfAllHasVarians) setIsAllowToAddToCart(true);
    } else {
      setIsAllowToAddToCart(false);
    }
  }, [filteredVariantOptions, setIsAllowToAddToCart]);

  const updateDisabledOptions = (newSelectedOptions: Record<string, string>) => {
    setDisabledOptions([]);
  };

  const allVariantsSelected = filteredVariantOptions?.every(
    (variant) => variant?.integrationId && selectedOptions.hasOwnProperty(variant.integrationId),
  );

  // const renderImage: string[] = isDigitalCard
  //   ? (productInfo.images?.map((i) => i.url) ?? []).filter((url): url is string => !!url)
  //   : (
  //       currentVariant?.images?.map((i) => buildCDNImageUrl(i.url ?? '')) ??
  //       productInfo.images?.map((i) => buildCDNImageUrl(i.url ?? '')) ??
  //       []
  //     ).filter((url): url is string => !!url);

  // const renderMobileImage: { image: string }[] = isDigitalCard
  //   ? (productInfo?.images?.map((i) => ({ image: i.url ?? '' })) ?? [])
  //   : (currentVariant?.images?.map((i) => ({ image: buildCDNImageUrl(i.url ?? '') })) ??
  //     productInfo?.images?.map((i) => ({ image: buildCDNImageUrl(i.url ?? '') })) ??
  //     []);

  const getImages = () => {
    const sourceImages = isDigitalCard
      ? productInfo.images
      : (currentVariant?.images ?? productInfo.images);

    if (isDigitalCard) {
      // رجّع الصور زي ما هي بدون buildCDNImageUrl
      return sourceImages?.map((i) => i.url ?? '') ?? [];
    }

    // استخدم buildCDNImageUrl لو مش Digital Card
    return sourceImages?.map((i) => buildCDNImageUrl(i.url ?? '')) ?? [];
  };

  const renderImage: string[] = getImages();

  const renderMobileImage: { image: string }[] = getImages().map((url) => ({
    image: url,
  }));

  return (
    <div
      className={
        'styled-scroll mt-5 overflow-y-auto bg-linear-to-b from-[#FEFDFE] from-0% to-transparent to-100%'
      }
    >
      <div className="grid grid-cols-12">
        <div id="images-gallery" className="col-span-6 hidden h-full overflow-hidden lg:block">
          <ProductCarouselDesktop slides={renderImage} name={productInfo.englishName} />
        </div>
        <div id="images-gallery" className="col-span-12 block h-full basis-1/4 lg:hidden">
          <LargeCarousel slides={renderMobileImage} name={productInfo.englishName} />
        </div>

        <div className="col-span-12 w-full items-start justify-start lg:col-span-6">
          <div
            id="product-info"
            className="bg-gradent-to-t flex h-full w-full flex-col gap-5 from-white from-50% to-violet-100 to-100% px-2 pt-2 lg:px-4 lg:pt-6"
          >
            <div id="product-card" className="flex h-fit max-h-fit w-full flex-col gap-3">
              <div className="flex w-full flex-row items-center justify-between">
                <h1 className="w-full text-xl leading-11 font-semibold text-zinc-800 lg:text-2xl">
                  {localActive === 'ar' ? productInfo?.arabicName : productInfo?.englishName}
                </h1>
              </div>
              <div className="flex items-start justify-start gap-2">
                <p className="hidden w-full max-w-md leading-relaxed font-normal text-zinc-800">
                  {localActive === 'ar'
                    ? productInfo?.arabicDescription
                    : productInfo?.englishDescription}
                </p>
                {productInfo?.discount ? (
                  <div className={'w-20 rounded-md bg-rose-100 px-1.5 py-1 transition-all'}>
                    <p className="text-center text-xs text-rose-600 capitalize">{t('sale')}</p>
                  </div>
                ) : (
                  <div className="basis-1/4" />
                )}
              </div>
              <div className="flex gap-1 text-base">
                <h3 className="font-extrabold text-zinc-600">
                  {localActive === 'ar'
                    ? productInfo?.store?.arabicName
                    : productInfo?.store?.englishName}
                </h3>
                <span>|</span>
                <span className="text-zinc-500">
                  {localActive === 'ar'
                    ? productInfo?.branch?.arabicName
                    : productInfo?.branch?.englishName}
                </span>
              </div>

              {currentVariant ? (
                <div className="flex items-center gap-2">
                  <div className="text-xl leading-8.5 font-bold text-zinc-800 lg:text-2xl">
                    <SaudiRealls
                      price={Number(
                        (currentVariant.price && currentVariant.price * quantity)?.toFixed(2),
                      )}
                      size="large"
                      currency={productInfo?.branch?.currencyCode || productInfo?.currencyCode}
                      countryId={productInfo?.branch?.countryId || productInfo?.countryId}
                    />
                  </div>
                  {currentVariant.discount && (
                    <>
                      <div className="leading-relaxed text-zinc-500 line-through">
                        <SaudiRealls
                          price={Number(
                            (
                              currentVariant.originalPrice &&
                              currentVariant.originalPrice * quantity
                            )?.toFixed(2),
                          )}
                          size="medium"
                          currency={productInfo?.branch?.currencyCode || productInfo?.currencyCode}
                          countryId={productInfo?.branch?.countryId || productInfo?.countryId}
                        />
                      </div>
                      <Typography
                        className="flex items-center gap-1 rounded-md bg-rose-600 px-1"
                        color={'white'}
                        weight={'semibold'}
                        component={'div'}
                      >
                        {currentVariant?.discount?.discountValue ? (
                          <>
                            {currentVariant.discount.discountType === DiscountTypeEnums.Percentage
                              ? `${currentVariant.discount.discountValue}%`
                              : `${(currentVariant.discount.discountValue * quantity).toFixed(2)} `}
                            {currentVariant.discount.discountType !==
                              DiscountTypeEnums.Percentage && (
                              <SAR width={16} height={16} className="h-auto w-auto" />
                            )}
                            {t('Off')}
                          </>
                        ) : null}
                      </Typography>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h3 className="text-xl leading-8.5 font-bold text-zinc-800 lg:text-2xl">
                    <SaudiRealls
                      price={Number(
                        (productInfo?.price && productInfo?.price * quantity)?.toFixed(2),
                      )}
                      size="large"
                      currency={productInfo?.branch?.currencyCode || productInfo?.currencyCode}
                      countryId={productInfo?.branch?.countryId || productInfo?.countryId}
                    />
                  </h3>
                  {productInfo?.discount && (
                    <>
                      <h2 className="leading-relaxed text-zinc-500">
                        <SaudiRealls
                          price={Number(
                            (
                              productInfo?.originalPrice && productInfo?.originalPrice * quantity
                            )?.toFixed(2),
                          )}
                          size="medium"
                          isHaveDiscount={true}
                          currency={productInfo?.branch?.currencyCode || productInfo?.currencyCode}
                          countryId={productInfo?.branch?.countryId || productInfo?.countryId}
                        />
                      </h2>
                      <Typography
                        className="flex items-center gap-1 rounded-md bg-rose-600 px-1"
                        color={'white'}
                        weight={'semibold'}
                        component={'div'}
                      >
                        {productInfo?.discount?.discountValue ? (
                          productInfo?.discount?.discountType === DiscountTypeEnums.Percentage ? (
                            <Typography
                              color={'white'}
                              weight={'semibold'}
                              component={'span'}
                              className="flex items-center"
                            >
                              {productInfo.discount.discountValue} %
                            </Typography>
                          ) : (
                            <Typography
                              color={'white'}
                              weight={'semibold'}
                              className="flex items-center"
                              component={'span'}
                            >
                              <SAR width={16} height={16} className="h-auto w-auto" />
                              {productInfo.discount.discountValue * quantity}{' '}
                            </Typography>
                          )
                        ) : null}
                        {t('Off')}
                      </Typography>
                    </>
                  )}
                </div>
              )}

              {filteredVariantOptions && filteredVariantOptions?.length > 0 && (
                <div className="flex flex-col gap-5 pt-4 pb-6">
                  {filteredVariantOptions?.map((variant, index) => {
                    if (!variant?.variantOptionValues || variant.variantOptionValues.length === 0) {
                      return null;
                    }

                    return (
                      <div key={index}>
                        <div className="flex gap-1">
                          <p className="text-xl leading-8.5 font-semibold text-zinc-800">
                            {localActive === 'ar' ? variant?.arabicName : variant?.englishName}
                          </p>
                        </div>
                        <div className="styled-scroll flex items-center justify-start gap-2 overflow-x-auto">
                          {variant?.variantOptionValues?.map((option, index) => {
                            const queryKey = variant?.englishName?.toLowerCase() ?? '';
                            let queryValue = option.englishName ?? '';
                            const isSelected = queryValue === params.get(queryKey);
                            const unselected = unselectedVariants.includes(
                              variant?.integrationId as string,
                            );
                            const isDisabled = disabledOptions.includes(
                              option.integrationId as string,
                            );

                            return (
                              <button
                                key={index}
                                onClick={(e) => {
                                  // Create a copy of the current selected options and update it
                                  const newSelectedOptions = {
                                    ...selectedOptions,
                                    [variant?.integrationId as string]: option.integrationId,
                                  } as Record<string, string>;
                                  // Check if the new selected options are valid
                                  const isValid = checkSelectedOptions(newSelectedOptions);
                                  if (isValid) {
                                    setSelectedOptions(newSelectedOptions);
                                    setUnselectedVariants((prev) =>
                                      prev.filter((id) => id !== variant.integrationId),
                                    );
                                    updateDisabledOptions(newSelectedOptions);
                                    handleSelect(variant, option);
                                  } else {
                                    setDisabledOptions((prev) => [
                                      ...prev,
                                      option.integrationId as string,
                                    ]);
                                  }
                                  return e;
                                }}
                                className={`${
                                  isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                                } ${
                                  isSelected
                                    ? 'border-primary bg-primary text-white'
                                    : unselected
                                      ? isDisabled
                                        ? ''
                                        : 'border-red-500'
                                      : 'border-violet-100'
                                } text-primary flex min-w-max cursor-pointer items-center justify-center rounded-xl border px-2 py-1 font-semibold transition-all duration-150`}
                              >
                                <CurrentLanguageText
                                  arabicName={`${option?.arabicName}`}
                                  englishName={`${option?.englishName}`}
                                />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!allVariantsSelected && error && (
                <Alert variant={'warning'}>
                  <AlertDescription className="flex items-center gap-3">
                    <AlertCircle className="h-4 w-4" />
                    {t('SelectOptions')}
                  </AlertDescription>
                </Alert>
              )}

              {/* start quantity */}

              {!isDigitalCard && (
                <div
                  className={`border-primary-50/50 bg-primary-50/50 flex items-end gap-6 rounded-full border px-2 py-2 shadow-sm backdrop-blur-[25px] ltr:self-end rtl:self-start`}
                  dir="ltr"
                >
                  <div className="flex items-center justify-center">
                    <button
                      id="plus"
                      type="button"
                      aria-label="Add product"
                      onClick={() => {
                        setQuantity((i) => i + 1);
                        trackEvent(AmplitudeEvents.product_quantity_increased, {
                          shop_id: Number(productInfo?.store?.id),
                          shop_name: `${productInfo?.store?.englishName}`,
                          product_id: `${productInfo.integrationId}`,
                          product_name: `${localActive === 'ar' ? productInfo?.arabicName : productInfo?.englishName}`,
                          product_price: Number(
                            (
                              currentVariant?.price && currentVariant.price * (quantity + 1)
                            )?.toFixed(2),
                          ),
                          product_quantity: quantity + 1,
                        });
                      }}
                      className={`${quantity === 10 ? 'text-gray-5 pointer-events-none' : ''} ease-lineard text-3xl transition-all duration-150 focus:outline-hidden`}
                    >
                      <Plus />
                    </button>

                    <div className="pointer-events-none w-8 bg-transparent text-center text-lg font-medium text-[#272728]">
                      {quantity}
                    </div>
                    <button
                      id="minus"
                      type="button"
                      aria-label="Remove product"
                      onClick={() => {
                        setQuantity((i) => (i === 1 ? 1 : i - 1));
                        trackEvent(AmplitudeEvents.product_quantity_decreased, {
                          shop_id: Number(productInfo?.store?.id),
                          shop_name: `${productInfo?.store?.englishName}`,
                          product_id: `${productInfo.integrationId}`,
                          product_name: `${localActive === 'ar' ? productInfo?.arabicName : productInfo?.englishName}`,
                          product_price: Number(
                            (
                              currentVariant?.price && currentVariant.price * (quantity - 1)
                            )?.toFixed(2),
                          ),
                          product_quantity: quantity - 1,
                        });
                      }}
                      className="transition-all duration-150 ease-linear focus:outline-hidden"
                    >
                      <Minus />
                    </button>
                  </div>
                </div>
              )}

              {/* start add to card and add to faviort  */}
              <div className="flex items-center gap-3">
                {data ? (
                  <div className="flex w-full">
                    {productInfo?.branch?.id && (
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full">
                        <IsFavorite
                          branch_id={productInfo?.branch?.id}
                          type={1}
                          item_id={`${productInfo.integrationId}`}
                        />
                      </div>
                    )}

                    <div className="flex w-full">
                      <AddToCartButton
                        onClick={handleAddCard}
                        disabled={!!productInfo?.isSoldOut || !!currentVariant?.isSoldOut}
                        imageSrc={renderImage?.[0]}
                        idleText={
                          productInfo?.isSoldOut || !!currentVariant?.isSoldOut
                            ? t('SoldOut')
                            : cartItem
                              ? t('update-cart')
                              : t('add-to-cart')
                        }
                        successText={cartItem ? t('update-cart') : t('ProductAddedSuccessfully')}
                        className="mx-auto flex w-full items-center justify-center gap-2 self-center rounded-full py-3 leading-relaxed font-semibold transition"
                      />
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={() => signIn('keycloak')}
                    type="button"
                    rounded={'full'}
                    className="mx-auto flex w-full items-center justify-center gap-2 self-center rounded-full py-3 leading-relaxed font-semibold transition hover:opacity-50"
                  >
                    <span> {t('add-to-cart')} </span>
                  </Button>
                )}
              </div>
            </div>

            {/* store details  */}

            {productInfo?.branch?.id && (
              <div
                id="store-card"
                className="mt-5 h-full max-h-77.5 w-full flex-col gap-3 rounded-lg border border-[#ECE9FF] bg-white p-4 pb-6 shadow-sm backdrop-blur-[25px]"
              >
                <p className="leading-relaxed font-semibold text-zinc-800">
                  {t('this-product-is-sold-by')}:
                </p>
                <div className="my-3 flex flex-row items-start justify-start gap-4">
                  <div className="h-24 w-24 rounded-sm border border-transparent bg-white p-2">
                    <Image
                      src={`${productInfo?.store?.logoUrl}`}
                      size={PRODUCT_IMAGE_SIZE.MEDIUM}
                      width={85}
                      height={40}
                      alt={`${productInfo?.store?.englishName}`}
                      className="h-full w-full rounded-sm object-scale-down"
                    />
                  </div>
                  <div className="flex h-full basis-2/3 flex-col items-start justify-center gap-1">
                    <p className="mt-2 text-lg leading-7 font-semibold text-zinc-800">
                      {localActive === 'ar'
                        ? productInfo?.store?.arabicName
                        : productInfo?.store?.englishName}
                    </p>
                    <p className="mt-2 text-sm leading-7 font-normal text-zinc-800">
                      {localActive === 'ar'
                        ? productInfo?.branch?.arabicName
                        : productInfo?.branch?.englishName}
                    </p>
                  </div>
                </div>
                <p className="my-4 leading-relaxed font-semibold text-zinc-800">
                  {t('you-are-shopping-from')}:
                </p>
                <Link href={Routes.branchDetails(productInfo?.store?.id, productInfo?.store?.name)}>
                  <button
                    type={'button'}
                    className="text-md hover:border-primary/50 hover:bg-primary/15 mx-auto flex w-full items-center justify-center gap-1 rounded-lg border border-[#F3F3F3] py-2 leading-normal font-extrabold text-zinc-800 transition-all"
                  >
                    <span>
                      {localActive === 'ar'
                        ? productInfo?.branch?.arabicName
                        : productInfo?.branch?.englishName}
                    </span>
                  </button>
                </Link>
                <div className={'w-full py-5 text-center'}>
                  <Link
                    href={Routes.branchDetails(productInfo?.store?.id, productInfo?.store?.name)}
                    className="text-primary mx-auto mt-5 w-full justify-center text-center leading-relaxed font-semibold underline underline-offset-8 transition-all hover:font-bold"
                  >
                    {t('view-store')}
                  </Link>
                </div>
              </div>
            )}

            <div className="h-full min-h-50 w-full">
              {localActive === 'ar'
                ? productInfo?.arabicDescription && (
                    <AccordionList
                      questions={[
                        {
                          question: t('Product Description'),
                          answer: productInfo?.arabicDescription,
                        },
                      ]}
                    />
                  )
                : productInfo?.englishDescription && (
                    <AccordionList
                      questions={[
                        {
                          question: t('Product Description'),
                          answer: productInfo.englishDescription,
                        },
                      ]}
                    />
                  )}
            </div>
          </div>
        </div>
      </div>

      {productInfo?.similarProducts?.length !== 0 && (
        <SimilarProducts
          similarProductsInfo={productInfo?.similarProducts}
          branchId={productInfo?.branch?.id}
          englishStoreName={productInfo?.store?.englishName}
          storeArabicName={productInfo?.store?.arabicName}
          englishBranchName={
            localActive === 'ar'
              ? productInfo?.branch?.arabicName
              : productInfo?.branch?.englishName
          }
          currencyCode={productInfo?.branch?.currencyCode || productInfo?.currencyCode}
          countryId={productInfo?.branch?.countryId || productInfo?.countryId}
        />
      )}
    </div>
  );
};
